import { TabletButton } from '@campfire/tablet-button';
import { Box, Switch, Theme, Typography } from '@material-ui/core';
import { Sync, Lock } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { isEqual } from 'lodash';
import React, { useMemo, useState, useEffect } from 'react';
import { AlertCard } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ORG_CHAT_SETTINGS } from './admin-console-chat-model.gql';
import { useAdminConsoleChatActions } from './admin-console-chat-actions';
import { GetOrgChatSettings } from './__generated__/GetOrgChatSettings';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
      display: 'flex',
      justifyContent: 'space-between',
    },
    headingLeft: {
      flex: 1,
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    sectionHeading: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginBottom: '1rem',
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
    toggleRow: {
      display: 'flex',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
    },
    toggleLabel: {
      fontSize: '15px',
      color: '#000000b0',
      fontWeight: 500,
    },
    toggleDescription: {
      fontSize: '13px',
      color: '#808080',
    },
    upgradeContainer: {
      background: 'white',
      padding: '3rem 1.875rem',
      marginTop: '2.25rem',
      textAlign: 'center',
      borderRadius: '4px',
    },
    upgradeIconWrap: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      background: '#F3E8FF',
      marginBottom: '1rem',
    },
    upgradeTitle: {
      fontWeight: 600,
      fontSize: '20px',
      color: theme.color.grey.neutralBrand900,
      marginBottom: '0.5rem',
    },
    upgradeBody: {
      fontSize: '14px',
      color: '#808080',
      maxWidth: '560px',
      margin: '0 auto 1.5rem',
    },
  })
);

type FormValues = {
  chatAutoChannelProgram: boolean;
  chatAutoChannelActivity: boolean;
  chatAutoChannelRole: boolean;
};

const INITIAL_FALLBACK: FormValues = {
  chatAutoChannelProgram: false,
  chatAutoChannelActivity: false,
  chatAutoChannelRole: false,
};

const UPGRADE_CONTACT_EMAIL = 'hello@volaby.org';

export const AdminConsoleChatSettings = () => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const { runSaveChatSettings, runSaveChatSettingsLoading } = useAdminConsoleChatActions();

  const { data, refetch } = useCampfireQuery<GetOrgChatSettings, undefined>(GET_ORG_CHAT_SETTINGS);

  const chatEnabled = !!data?.tm.tenant.config?.chatEnabled;

  const initialValues: FormValues = useMemo(() => {
    const config = data?.tm.tenant.config;
    if (!config) return INITIAL_FALLBACK;
    return {
      chatAutoChannelProgram: !!config.chatAutoChannelProgram,
      chatAutoChannelActivity: !!config.chatAutoChannelActivity,
      chatAutoChannelRole: !!config.chatAutoChannelRole,
    };
  }, [data]);

  const [values, setValues] = useState<FormValues>(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const hasChanges = !isEqual(values, initialValues);

  const handleToggle = (key: keyof FormValues) => {
    setValues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    runSaveChatSettings({
      chatEnabled,
      ...values,
      handleSuccess: () => refetch && refetch(),
    });
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box className={classes.headingLeft}>
          <Typography variant='h2' className={classes.headingTitle}>
            Chat
          </Typography>
          <Typography variant='body2'>
            Enable in-app messaging between your volunteers and automatically create channels for programs, activities
            and roles.
          </Typography>
        </Box>
      </Box>

      {!chatEnabled ? (
        <Box className={classes.upgradeContainer}>
          <Box className={classes.upgradeIconWrap}>
            <Lock style={{ fontSize: 32, color: theme.color.secondary.main900 }} />
          </Box>
          <Typography className={classes.upgradeTitle}>Want Chat?</Typography>
          <Typography className={classes.upgradeBody}>
            Chat is an add-on that lets your volunteers message each other in-app, with automatic channels for rosters,
            programs, activities and roles. Get in touch to upgrade your plan.
          </Typography>
          <TabletButton
            variant='contained'
            color='primary'
            href={`mailto:${UPGRADE_CONTACT_EMAIL}?subject=Chat%20upgrade%20enquiry`}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              borderRadius: '4px',
              textTransform: 'none',
              padding: '8px 20px',
            }}
          >
            Upgrade to Chat
          </TabletButton>
        </Box>
      ) : (
        <>
          <Box className={classes.contentContainer}>
            <Typography className={classes.sectionHeading}>
              <Sync style={{ fontSize: '14px', paddingRight: '5px' }} /> Auto-Created Channels
            </Typography>
            <Typography className={classes.sectionDescription}>
              Automatically create a dedicated chat channel whenever a new program, activity or role is created. Members
              are kept in sync when enrolments change.
            </Typography>

            <AlertCard variant='info'>
              <Typography className={classes.infoBody}>
                Toggling any of these on will create channels for new entities going forward. Use the backfill button
                next to each toggle to retroactively create channels for existing entities.
              </Typography>
            </AlertCard>

            <Box className={classes.toggleRow}>
              <Switch
                checked={values.chatAutoChannelProgram}
                onChange={() => handleToggle('chatAutoChannelProgram')}
                color='primary'
                inputProps={{ 'aria-label': 'auto channels for programs' }}
              />
              <Box ml={1} flex={1}>
                <Typography className={classes.toggleLabel}>Programs</Typography>
                <Typography className={classes.toggleDescription}>
                  Create a channel for each program; enrolment follows program enrolment.
                </Typography>
              </Box>
            </Box>

            <Box className={classes.toggleRow}>
              <Switch
                checked={values.chatAutoChannelActivity}
                onChange={() => handleToggle('chatAutoChannelActivity')}
                color='primary'
                inputProps={{ 'aria-label': 'auto channels for activities' }}
              />
              <Box ml={1} flex={1}>
                <Typography className={classes.toggleLabel}>Activities</Typography>
                <Typography className={classes.toggleDescription}>
                  Create a channel for each activity; enrolment follows the activity team.
                </Typography>
              </Box>
            </Box>

            <Box className={classes.toggleRow}>
              <Switch
                checked={values.chatAutoChannelRole}
                onChange={() => handleToggle('chatAutoChannelRole')}
                color='primary'
                inputProps={{ 'aria-label': 'auto channels for roles' }}
              />
              <Box ml={1} flex={1}>
                <Typography className={classes.toggleLabel}>Roles</Typography>
                <Typography className={classes.toggleDescription}>
                  Create a channel for each role; enrolment follows volunteers assigned to that role.
                </Typography>
              </Box>
            </Box>
          </Box>

          {hasChanges ? (
            <Box display='flex' justifyContent='right' pt={2} pb={4}>
              <TabletButton
                type='button'
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={runSaveChatSettingsLoading}
                style={{
                  backgroundColor: theme.color.secondary.main900,
                  color: '#ffffff',
                  borderRadius: '4px',
                  textTransform: 'none',
                  padding: '7px 12px',
                  minWidth: '40px',
                }}
              >
                Save
              </TabletButton>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};
